import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TemplateManager = ({ currentLanguage, onSaveTemplate }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('fr');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateContent, setTemplateContent] = useState('');

  const labels = {
    fr: {
      title: 'Gestion des modèles',
      createNew: 'Nouveau modèle',
      templateName: 'Nom du modèle',
      category: 'Catégorie',
      language: 'Langue',
      subject: 'Objet',
      content: 'Contenu',
      save: 'Enregistrer',
      cancel: 'Annuler',
      templates: 'Modèles disponibles',
      edit: 'Modifier',
      delete: 'Supprimer',
      preview: 'Aperçu'
    },
    ar: {
      title: 'إدارة القوالب',
      createNew: 'قالب جديد',
      templateName: 'اسم القالب',
      category: 'الفئة',
      language: 'اللغة',
      subject: 'الموضوع',
      content: 'المحتوى',
      save: 'حفظ',
      cancel: 'إلغاء',
      templates: 'القوالب المتاحة',
      edit: 'تعديل',
      delete: 'حذف',
      preview: 'معاينة'
    }
  };

  const categoryOptions = [
    { value: '', label: currentLanguage === 'fr' ? 'Sélectionner une catégorie' : 'اختر فئة' },
    { value: 'document', label: currentLanguage === 'fr' ? 'Documents' : 'الوثائق' },
    { value: 'travel', label: currentLanguage === 'fr' ? 'Voyage' : 'السفر' },
    { value: 'visa', label: currentLanguage === 'fr' ? 'Visa' : 'التأشيرة' },
    { value: 'payment', label: currentLanguage === 'fr' ? 'Paiement' : 'الدفع' },
    { value: 'notification', label: currentLanguage === 'fr' ? 'Notification' : 'الإشعار' }
  ];

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' }
  ];

  const existingTemplates = [
    {
      id: 't1',
      name: 'Demande de documents',
      nameAr: 'طلب وثائق',
      category: 'document',
      language: 'fr',
      usageCount: 45
    },
    {
      id: 't2',
      name: 'Rappel de voyage',
      nameAr: 'تذكير بالسفر',
      category: 'travel',
      language: 'fr',
      usageCount: 32
    },
    {
      id: 't3',
      name: 'تحديث التأشيرة',
      nameAr: 'تحديث التأشيرة',
      category: 'visa',
      language: 'ar',
      usageCount: 28
    },
    {
      id: 't4',
      name: 'Rappel de paiement',
      nameAr: 'تذكير بالدفع',
      category: 'payment',
      language: 'fr',
      usageCount: 56
    }
  ];

  const handleSave = () => {
    const newTemplate = {
      name: templateName,
      category: templateCategory,
      language: templateLanguage,
      subject: templateSubject,
      content: templateContent,
      timestamp: new Date()?.toISOString()
    };
    
    onSaveTemplate?.(newTemplate);
    setIsCreating(false);
    setTemplateName('');
    setTemplateCategory('');
    setTemplateSubject('');
    setTemplateContent('');
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-secondary" />
          </div>
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            {labels?.[currentLanguage]?.title}
          </h2>
        </div>
        {!isCreating && (
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsCreating(true)}
          >
            {labels?.[currentLanguage]?.createNew}
          </Button>
        )}
      </div>
      {isCreating ? (
        <div className="space-y-4">
          <Input
            label={labels?.[currentLanguage]?.templateName}
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e?.target?.value)}
            placeholder={currentLanguage === 'fr' ? 'Nom du modèle' : 'اسم القالب'}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={labels?.[currentLanguage]?.category}
              options={categoryOptions}
              value={templateCategory}
              onChange={setTemplateCategory}
              required
            />

            <Select
              label={labels?.[currentLanguage]?.language}
              options={languageOptions}
              value={templateLanguage}
              onChange={setTemplateLanguage}
              required
            />
          </div>

          <Input
            label={labels?.[currentLanguage]?.subject}
            type="text"
            value={templateSubject}
            onChange={(e) => setTemplateSubject(e?.target?.value)}
            placeholder={currentLanguage === 'fr' ? 'Objet du message' : 'موضوع الرسالة'}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {labels?.[currentLanguage]?.content}
            </label>
            <textarea
              value={templateContent}
              onChange={(e) => setTemplateContent(e?.target?.value)}
              rows={8}
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              placeholder={currentLanguage === 'fr' ? 'Contenu du modèle...' : 'محتوى القالب...'}
              dir={templateLanguage === 'ar' ? 'rtl' : 'ltr'}
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsCreating(false)}
              className="flex-1"
            >
              {labels?.[currentLanguage]?.cancel}
            </Button>
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
              className="flex-1"
            >
              {labels?.[currentLanguage]?.save}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {existingTemplates?.map((template) => (
            <div
              key={template?.id}
              className="border border-border rounded-lg p-4 hover:shadow-warm transition-smooth"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-foreground mb-1">
                    {currentLanguage === 'fr' ? template?.name : template?.nameAr}
                  </h3>
                  <div className="flex items-center gap-3 caption text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Icon name="Tag" size={14} />
                      {categoryOptions?.find(c => c?.value === template?.category)?.label}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Icon name="Languages" size={14} />
                      {template?.language === 'fr' ? 'Français' : 'العربية'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Icon name="BarChart3" size={14} />
                      {template?.usageCount} {currentLanguage === 'fr' ? 'utilisations' : 'استخدام'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="p-2 rounded-md hover:bg-muted transition-smooth">
                    <Icon name="Eye" size={18} className="text-muted-foreground" />
                  </button>
                  <button className="p-2 rounded-md hover:bg-muted transition-smooth">
                    <Icon name="Edit" size={18} className="text-muted-foreground" />
                  </button>
                  <button className="p-2 rounded-md hover:bg-destructive/10 transition-smooth">
                    <Icon name="Trash2" size={18} className="text-destructive" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateManager;